import { EnvironmentGroupId } from '@/react/portainer/environments/types';

import { Directive } from '../../../types';

/**
 * Complete form values for Docker Security Policy form.
 * Combines common policy fields (name, environmentGroupIds) with type-specific fields.
 * This is the type used by Formik in DockerSecurityPolicyForm.
 */
export interface DockerSecurityFormValues {
  /** Policy name */
  name: string;
  /** Environment groups to apply the policy to */
  environmentGroupIds: Array<EnvironmentGroupId>;
  /** Allow regular users to create bind mounts */
  allowBindMountsForRegularUsers: boolean;
  /** Allow regular users to run containers in privileged mode */
  allowPrivilegedModeForRegularUsers: boolean;
  /** Allow regular users to access host namespaces (PID, IPC, network) */
  allowHostNamespaceForRegularUsers: boolean;
  /** Allow regular users to map host devices into containers */
  allowDeviceMappingForRegularUsers: boolean;
  /** Allow regular users to manage Docker stacks */
  allowStackManagementForRegularUsers: boolean;
  /** Allow regular users to add/drop container capabilities */
  allowContainerCapabilitiesForRegularUsers: boolean;
  /** Allow regular users to modify sysctl settings */
  allowSysctlSettingForRegularUsers: boolean;
  /** Allow regular users to use security-opt settings */
  allowSecurityOptForRegularUsers: boolean;
}

/**
 * Type-specific form data for Docker Security Policy (without common fields).
 * Used by inner form components.
 */
export interface DockerSecurityFormData {
  /** Allow regular users to create bind mounts */
  allowBindMountsForRegularUsers: boolean;
  /** Allow regular users to run containers in privileged mode */
  allowPrivilegedModeForRegularUsers: boolean;
  /** Allow regular users to access host namespaces (PID, IPC, network) */
  allowHostNamespaceForRegularUsers: boolean;
  /** Allow regular users to map host devices into containers */
  allowDeviceMappingForRegularUsers: boolean;
  /** Allow regular users to manage Docker stacks */
  allowStackManagementForRegularUsers: boolean;
  /** Allow regular users to add/drop container capabilities */
  allowContainerCapabilitiesForRegularUsers: boolean;
  /** Allow regular users to modify sysctl settings */
  allowSysctlSettingForRegularUsers: boolean;
  /** Allow regular users to use security-opt settings */
  allowSecurityOptForRegularUsers: boolean;
}

/**
 * Stored policy data structure (from backend)
 * This is what comes back from the API in policy.Data when reading a policy
 * Uses camelCase (backend stores in this format)
 */
export interface DockerSecurityPolicyData {
  allowBindMountsForRegularUsers: Directive<boolean>;
  allowPrivilegedModeForRegularUsers: Directive<boolean>;
  allowHostNamespaceForRegularUsers: Directive<boolean>;
  allowDeviceMappingForRegularUsers: Directive<boolean>;
  allowStackManagementForRegularUsers: Directive<boolean>;
  allowContainerCapabilitiesForRegularUsers: Directive<boolean>;
  allowSysctlSettingForRegularUsers: Directive<boolean>;
  allowSecurityOptForRegularUsers: Directive<boolean>;
}

/**
 * API payload for Docker Security Policy
 * This is what we send to the backend when creating/updating (API representation)
 * Uses camelCase for consistency with frontend conventions
 */
export interface DockerSecurityPolicyPayload {
  allowBindMountsForRegularUsers: Directive<boolean>;
  allowPrivilegedModeForRegularUsers: Directive<boolean>;
  allowHostNamespaceForRegularUsers: Directive<boolean>;
  allowDeviceMappingForRegularUsers: Directive<boolean>;
  allowStackManagementForRegularUsers: Directive<boolean>;
  allowContainerCapabilitiesForRegularUsers: Directive<boolean>;
  allowSysctlSettingForRegularUsers: Directive<boolean>;
  allowSecurityOptForRegularUsers: Directive<boolean>;
}

/**
 * Default initial values for Docker Security Policy type-specific fields (create mode)
 */
export const DOCKER_SECURITY_INITIAL_VALUES: DockerSecurityFormData = {
  allowBindMountsForRegularUsers: true,
  allowPrivilegedModeForRegularUsers: true,
  allowHostNamespaceForRegularUsers: true,
  allowDeviceMappingForRegularUsers: true,
  allowStackManagementForRegularUsers: true,
  allowContainerCapabilitiesForRegularUsers: true,
  allowSysctlSettingForRegularUsers: true,
  allowSecurityOptForRegularUsers: true,
};

/**
 * Default initial values for complete Docker Security Policy form (create mode)
 */
export const DOCKER_SECURITY_FORM_INITIAL_VALUES: DockerSecurityFormValues = {
  name: '',
  environmentGroupIds: [],
  ...DOCKER_SECURITY_INITIAL_VALUES,
};
